import { BaseAPI } from 'API/BaseApi';
import { eAPITags } from 'API/eAPITags';
import { IEscrowWireDisbursementsData } from 'types/order/escrow/IEscrowWireDisbursementsData';
import { IWireRoutingResponse } from 'types/order/escrow/IWireRoutingResponse';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import {
  ICreateWireDisbursementRequest,
  IUpdateWireDisbursementRequest,
} from 'types/order/escrow/request/IWireDisbursementRequest';
import { IWireDisbursementFormData } from 'types/order/escrow/wires/IWireDisbursementFormData';
import { ICreateWireFromACHPushRequest } from '../../../components/order/escrow/disbursements/achPushDisbursements/createWireFromACH/interfaces/ICreateWireFromACHPushRequest';
import { emptyFieldsToUndefined } from './EscrowBaseAPI';
import { IWireDisbursementVoidRequest } from 'types/accounting/IWireDisbursementVoidRequest';

export interface ICreateWireDisbursementProps {
  orderId: string;
  escrowFileId: string;
  newWireData: ICreateWireDisbursementRequest;
}

export interface IUpdateWireDisbursementProps {
  orderId: string;
  updateWireData: IUpdateWireDisbursementRequest;
}

export interface ICreateWireDisbursementFromACHRequest extends ICreateWireFromACHPushRequest {
  orderId: string;
}

const WireDisbursementAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    getWireDisbursement: build.query<IWireDisbursementFormData, { wireId: string }>({
      query: ({ wireId }) => ({
        url: `/timber/api/internal/escrow/wire_disbursement/${wireId}`,
        method: 'GET',
      }),
      transformResponse: (wire: IEscrowWireDisbursementsData) => ({
        ...wire,
        accountNumberConfirmation: wire.accountNumber,
        type: eDisbursementType.Wire,
      }),
      providesTags: (result, error, { wireId }) => [{ type: eAPITags.WireDisbursement, id: wireId }],
    }),
    createWireDisbursement: build.mutation<void, ICreateWireDisbursementProps>({
      query: ({ escrowFileId, newWireData: newDisbursementData }) => ({
        url: `/timber/api/internal/escrow/${escrowFileId}/wire_disbursements`,
        method: 'POST',
        body: emptyFieldsToUndefined(newDisbursementData),
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
      ],
    }),
    updateWireDisbursement: build.mutation<void, IUpdateWireDisbursementProps>({
      query: ({ updateWireData: body }) => ({
        url: `/timber/api/internal/escrow/wire_disbursements/${body.id}`,
        method: 'PUT',
        body: emptyFieldsToUndefined(body),
      }),
      invalidatesTags: (result, error, { orderId, updateWireData }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.WireDisbursement, id: updateWireData.id },
      ],
    }),
    sendWireDisbursement: build.mutation<void, { wireId: string; orderId: string }>({
      query: ({ wireId }) => ({
        url: `/timber/api/internal/escrow/wire_disbursements/${wireId}/send`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { orderId, wireId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.WireDisbursement, id: wireId },
      ],
    }),
    deleteWireDisbursement: build.mutation<void, { wireId: string; orderId: string }>({
      query: ({ wireId }) => ({
        url: `/timber/api/internal/escrow/wire_disbursements/${wireId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId, wireId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.WireDisbursement, id: wireId },
      ],
    }),
    voidWireDisbursement: build.mutation<void, { orderId: string; wire: IWireDisbursementVoidRequest }>({
      query: ({ wire }) => ({
        url: `/timber/api/internal/escrow/wire_disbursements/void`,
        method: 'POST',
        body: wire,
      }),
      invalidatesTags: (result, error, { orderId, wire }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.WireDisbursement, id: wire.id },
      ],
    }),
    getBankWireRoutingNumber: build.query<IWireRoutingResponse, string>({
      query: (routingNumber) => ({
        url: `/escrow/v1/wire_routing/${routingNumber}`,
        method: 'GET',
      }),
    }),
    createWireDisbursementFromACH: build.mutation<void, ICreateWireDisbursementFromACHRequest>({
      query: (wireData) => ({
        url: `/timber/api/internal/escrow/wire_disbursement/create_wire_from_ach_push`,
        method: 'POST',
        body: wireData,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
      ],
    }),
  }),
});

export const {
  useGetWireDisbursementQuery,
  useCreateWireDisbursementMutation,
  useUpdateWireDisbursementMutation,
  useDeleteWireDisbursementMutation,
  useSendWireDisbursementMutation,
  useGetBankWireRoutingNumberQuery,
  useCreateWireDisbursementFromACHMutation,
  useVoidWireDisbursementMutation,
} = WireDisbursementAPI;
